import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Activity, ChevronLeft, ChevronRight, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ActivityLogPage() {
  const { lang } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ user_id: '', role: '', entity_type: '', date_from: '', date_to: '' });

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: page.toString(), limit: '30' });
      if (filters.user_id) params.set('user_id', filters.user_id);
      if (filters.role) params.set('role', filters.role);
      if (filters.entity_type) params.set('entity_type', filters.entity_type);
      if (filters.date_from) params.set('date_from', filters.date_from);
      if (filters.date_to) params.set('date_to', filters.date_to);
      const res = await axios.get(`${API}/activity-log?${params}`, { withCredentials: true });
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch {}
  }, [page, filters]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const totalPages = Math.ceil(total / 30);

  const actionColors = {
    create: 'bg-green-100 text-green-700',
    status_change: 'bg-blue-100 text-blue-700',
    update: 'bg-amber-100 text-amber-700',
    delete: 'bg-red-100 text-red-700',
  };

  return (
    <DashboardLayout>
      <div data-testid="activity-log-page">
        <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A] font-heading mb-6">
          {lang === 'ar' ? 'سجل النشاطات' : 'Activity Log'}
        </h1>

        {/* Filters */}
        <Card className="border-slate-200 shadow-sm mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Select value={filters.user_id || 'all'} onValueChange={v => { setFilters({ ...filters, user_id: v === 'all' ? '' : v }); setPage(1); }}>
                <SelectTrigger className="h-9 text-sm" data-testid="filter-user">
                  <SelectValue placeholder={lang === 'ar' ? 'المستخدم' : 'User'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{lang === 'ar' ? 'جميع المستخدمين' : 'All Users'}</SelectItem>
                  {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filters.role || 'all'} onValueChange={v => { setFilters({ ...filters, role: v === 'all' ? '' : v }); setPage(1); }}>
                <SelectTrigger className="h-9 text-sm" data-testid="filter-role">
                  <SelectValue placeholder={lang === 'ar' ? 'الدور' : 'Role'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{lang === 'ar' ? 'الكل' : 'All Roles'}</SelectItem>
                  <SelectItem value="admin">{lang === 'ar' ? 'مدير' : 'Admin'}</SelectItem>
                  <SelectItem value="lawyer">{lang === 'ar' ? 'محامي' : 'Lawyer'}</SelectItem>
                  <SelectItem value="client">{lang === 'ar' ? 'عميل' : 'Client'}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.entity_type || 'all'} onValueChange={v => { setFilters({ ...filters, entity_type: v === 'all' ? '' : v }); setPage(1); }}>
                <SelectTrigger className="h-9 text-sm" data-testid="filter-type">
                  <SelectValue placeholder={lang === 'ar' ? 'النوع' : 'Type'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{lang === 'ar' ? 'الكل' : 'All Types'}</SelectItem>
                  <SelectItem value="case">{lang === 'ar' ? 'قضية' : 'Case'}</SelectItem>
                  <SelectItem value="document">{lang === 'ar' ? 'مستند' : 'Document'}</SelectItem>
                  <SelectItem value="invoice">{lang === 'ar' ? 'فاتورة' : 'Invoice'}</SelectItem>
                  <SelectItem value="user">{lang === 'ar' ? 'مستخدم' : 'User'}</SelectItem>
                </SelectContent>
              </Select>
              <Input type="date" value={filters.date_from} onChange={e => { setFilters({ ...filters, date_from: e.target.value }); setPage(1); }} className="h-9 text-sm" placeholder="From" data-testid="filter-date-from" />
              <Input type="date" value={filters.date_to} onChange={e => { setFilters({ ...filters, date_to: e.target.value }); setPage(1); }} className="h-9 text-sm" placeholder="To" data-testid="filter-date-to" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{lang === 'ar' ? 'الوقت' : 'Time'}</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{lang === 'ar' ? 'المستخدم' : 'User'}</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{lang === 'ar' ? 'الإجراء' : 'Action'}</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-[#94A3B8]">{lang === 'ar' ? 'التفاصيل' : 'Details'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map(log => (
                <TableRow key={log.log_id} className="border-b border-slate-100">
                  <TableCell className="text-xs text-[#94A3B8] whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</TableCell>
                  <TableCell>
                    <div>
                      <span className="text-sm text-[#0F172A]">{log.user_name}</span>
                      <span className={`ms-2 px-1.5 py-0.5 text-[10px] rounded capitalize ${log.user_role === 'admin' ? 'bg-[#0F172A] text-white' : log.user_role === 'lawyer' ? 'bg-[#B48E4B]/10 text-[#B48E4B]' : 'bg-slate-100 text-[#475569]'}`}>{log.user_role}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-0.5 rounded text-xs ${actionColors[log.action] || 'bg-slate-100 text-slate-600'}`}>
                      {log.action}
                    </span>
                    <span className="ms-2 text-xs text-[#94A3B8]">{log.entity_type}</span>
                  </TableCell>
                  <TableCell className="text-sm text-[#475569] max-w-xs truncate">{log.details}</TableCell>
                </TableRow>
              ))}
              {logs.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center py-12 text-[#94A3B8]">
                  <Activity className="w-10 h-10 mx-auto mb-2" />
                  {lang === 'ar' ? 'لا توجد نشاطات' : 'No activity logs yet'}
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-3 border-t border-slate-100">
              <span className="text-xs text-[#94A3B8]">{total} {lang === 'ar' ? 'سجل' : 'entries'}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
                <span className="text-sm text-[#475569] px-2 py-1">{page}/{totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}
